import React from 'react';
import { Link } from 'react-router-dom';
import { Target, ArrowLeft, User, Brain, Mail, BookOpen, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export default function AboutBuilder() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-slate-900">Archery Manager AI</span>
          </Link>
          <Link to="/login">
            <Button variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">About the Builder</h1>
          <p className="text-lg text-slate-600">The story behind Archery Manager AI</p>
        </div>

        <div className="space-y-8">
          {/* Section 1: Who Built This */}
          <Card className="border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-primary to-blue-600 p-6">
              <div className="flex items-center gap-3 text-white">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6" />
                </div>
                <h2 className="text-xl font-semibold">Who Built This</h2>
              </div>
            </div>
            <CardContent className="p-6 md:p-8">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">
                Built by an AI & Contact Center Engineering Leader
              </h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                Archery Manager AI was built by <strong>Vasanth Natarajan</strong>, an AI engineer and architect 
                with experience building large-scale, real-world AI systems across enterprise platforms.
              </p>
              <p className="text-slate-700 leading-relaxed">
                This project started as a personal solution to manage archery events, results, and performance 
                insights more effectively for youth and competitive archers.
              </p>
            </CardContent>
          </Card>

          {/* Section 2: Why AI? */}
          <Card className="border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6">
              <div className="flex items-center gap-3 text-white">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <Brain className="w-6 h-6" />
                </div>
                <h2 className="text-xl font-semibold">Why AI?</h2>
              </div>
            </div>
            <CardContent className="p-6 md:p-8">
              <p className="text-slate-700 leading-relaxed mb-4">
                AI is used to help organize data, surface patterns, and provide insights — <strong>not to replace 
                coaches or official scoring systems</strong>.
              </p>
              <p className="text-slate-700 leading-relaxed">
                The goal is to reduce manual tracking and help families and athletes focus on what matters most: 
                <span className="text-primary font-medium"> training and performance</span>.
              </p>
              
              <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-sm text-slate-600 italic">
                  "Technology should simplify, not complicate. Archery Manager AI aims to give archers and 
                  their families more time to focus on the sport they love."
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Section 3: Interested in AI Beyond Archery? */}
          <Card className="border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-6">
              <div className="flex items-center gap-3 text-white">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6" />
                </div>
                <h2 className="text-xl font-semibold">Interested in AI Beyond Archery?</h2>
              </div>
            </div>
            <CardContent className="p-6 md:p-8">
              <p className="text-slate-700 leading-relaxed mb-6">
                If you're interested in how modern AI systems are built — including <strong>GenAI</strong>, 
                <strong> Agentic AI</strong>, and <strong>real-world applications</strong> — you can optionally 
                subscribe to a separate AI newsletter curated by the builder.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="https://www.vasanthnatarajan.me/newsletter" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex-1"
                >
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="subscribe-newsletter-btn">
                    <Mail className="w-4 h-4 mr-2" />
                    Subscribe to AI Newsletter
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
                
                <a 
                  href="https://www.vasanthnatarajan.me/blog" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex-1"
                >
                  <Button variant="outline" className="w-full border-emerald-600 text-emerald-600 hover:bg-emerald-50" data-testid="read-blogs-btn">
                    <BookOpen className="w-4 h-4 mr-2" />
                    Read AI Blogs
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer Links */}
        <div className="mt-12 text-center text-sm text-slate-500">
          <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
          <span className="mx-2">•</span>
          <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
          <span className="mx-2">•</span>
          <Link to="/login" className="text-primary hover:underline">Back to Login</Link>
        </div>
      </main>
    </div>
  );
}
